import React, { useRef, useEffect, useState } from 'react';

export type Props = {
  imageUrl: string;
  secondaryImageUrl?: string;
  gapX: number;
  gapY: number;
  gapSize: Size;
};

type Size = { width: number; height: number };

/**
 * Allows placing child components at specific coordinate
 * props.gapX and props.gapY within provided image.
 *
 * Useful when we want to show artworks within specific
 * area of the given image. Assumes background-size: cover;
 * and is smart enough to know if image top and bottom is cropped
 * or left and right. Accordingly makes correct calculations.
 */
export const MaskedImage: React.FC<Props> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [image, setImageSize] = useState<Size | undefined>(undefined);
  const [container, setContainerSize] = useState<Size | undefined>(undefined);

  useEffect(() => {
    const storeContainerSizeToState = () => {
      const { current } = ref;
      const width = current ? current.offsetWidth : 0;
      const height = current ? current.offsetHeight : 0;
      if (width && height) {
        setContainerSize({ width, height });
      }
    };
    storeContainerSizeToState();
    window.addEventListener('resize', storeContainerSizeToState);
    return () => window.removeEventListener('resize', storeContainerSizeToState);
  }, [ref.current]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (img.width && img.height) {
        setImageSize({ width: img.width, height: img.height });
      }
    };
    img.src = props.imageUrl;
  }, [props.imageUrl]);

  let x: number | undefined;
  let y: number | undefined;
  let imageScale: number = 1;

  if (image && container) {
    imageScale = container.width / image.width;

    const imageRatio = image.width / image.height;
    const containerRatio = container.width / container.height;
    const isTopAndBottomCropped = containerRatio > imageRatio;

    if (isTopAndBottomCropped) {
      imageScale = container.width / image.width;
      x = props.gapX * imageScale;
      y = props.gapY * imageScale - (image.height * imageScale - container.height) / 2;
    } else {
      imageScale = container.height / image.height;
      // isLeftAndRightCropped
      x = props.gapX * imageScale - (image.width * imageScale - container.width) / 2;
      y = props.gapY * imageScale;
    }
  }

  return (
    <div
      ref={ref}
      className='stretch'
      style={{
        backgroundImage: `url(${props.imageUrl})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className='pos-rel w-100 h-100'>
        <div
          className='pos-abs fade'
          style={{
            width: props.gapSize.width * imageScale,
            height: props.gapSize.height * imageScale,
            left: x,
            top: y,
            background: '#000',
            opacity: image && container ? 1 : 0,
          }}
        >
          <div className='pos-rel w-100 h-100'>{props.children}</div>
        </div>
      </div>
      {props.secondaryImageUrl && (
        <div
          className='stretch'
          style={{
            backgroundImage: `url(${props.secondaryImageUrl})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}
    </div>
  );
};
